// @ts-nocheck
import {
  AGENT_SPEECH_LANGUAGES,
  DEFAULT_AGENT_SPEECH_LANGUAGE,
  CUSTOMER_NATIVE_MIN_CHROMIUM_VERSION,
  CUSTOMER_NATIVE_BLOCK_EDGE,
} from "@/utilities/constant";

export const AGENT_LANG_KEY = "cti_transcriptionLang";
export const CUSTOMER_LANG_KEY = "cti_customerNativeLang";

export const getSpeechRecognitionCtor = () => {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

export const isSpeechRecognitionSupported = () => !!getSpeechRecognitionCtor();

export const getChromiumMajorVersion = () => {
  if (typeof navigator === "undefined") return null;
  const brands = navigator.userAgentData?.brands;
  if (Array.isArray(brands)) {
    const match = brands.find((b) =>
      ["Chromium", "Google Chrome", "Microsoft Edge"].includes(b?.brand)
    );
    const version = Number(match?.version);
    if (Number.isFinite(version) && version > 0) return version;
  }
  const ua = navigator.userAgent || "";
  // Edg/ must be checked before Chrome/ since Edge's UA contains both.
  const uaMatch = ua.match(/(?:Edg|Chrome)\/(\d+)/);
  const uaVersion = uaMatch ? Number(uaMatch[1]) : NaN;
  return Number.isFinite(uaVersion) && uaVersion > 0 ? uaVersion : null;
};

export const isEdgeBrowser = () => {
  if (typeof navigator === "undefined") return false;
  const brands = navigator.userAgentData?.brands;
  if (Array.isArray(brands) && brands.some((b) => b?.brand === "Microsoft Edge")) {
    return true;
  }
  return /\bEdg(?:e|A|iOS)?\//.test(navigator.userAgent || "");
};

export const isSpeechRecognitionAudioTrackSupported = () => {
  if (!getSpeechRecognitionCtor()) return false;
  if (CUSTOMER_NATIVE_BLOCK_EDGE && isEdgeBrowser()) return false;
  const major = getChromiumMajorVersion();
  if (major == null) return false;
  if (major < CUSTOMER_NATIVE_MIN_CHROMIUM_VERSION) return false;
  return !isCustomerAudioTrackBlocked();
};

const AUDIO_TRACK_VERDICT_KEY = "cti_customerAudioTrackVerdict";

export const getBrowserBuildKey = () => {
  const brand = isEdgeBrowser() ? "edge" : "chromium";
  return `${brand}-${getChromiumMajorVersion() ?? "unknown"}`;
};

export const isCustomerAudioTrackBlocked = () => {
  try {
    return (
      window.localStorage.getItem(AUDIO_TRACK_VERDICT_KEY) ===
      `blocked:${getBrowserBuildKey()}`
    );
  } catch {
    return false;
  }
};

export const blockCustomerAudioTrack = () => {
  try {
    window.localStorage.setItem(
      AUDIO_TRACK_VERDICT_KEY,
      `blocked:${getBrowserBuildKey()}`
    );
  } catch {
    /* ignore persistence failure */
  }
};

/** Clears the learned verdict — exposed for support/debugging. */
export const clearCustomerAudioTrackVerdict = () => {
  try {
    window.localStorage.removeItem(AUDIO_TRACK_VERDICT_KEY);
  } catch {
    /* ignore */
  }
};

const isKnownAgentLang = (code) =>
  !!code && AGENT_SPEECH_LANGUAGES.some((l) => l.code === code);

export const getTranscriptionLang = () => {
  try {
    const stored = window.localStorage.getItem(AGENT_LANG_KEY);
    return isKnownAgentLang(stored) ? stored : DEFAULT_AGENT_SPEECH_LANGUAGE;
  } catch {
    return DEFAULT_AGENT_SPEECH_LANGUAGE;
  }
};

export const setTranscriptionLang = (code) => {
  if (!isKnownAgentLang(code)) return false;
  try {
    window.localStorage.setItem(AGENT_LANG_KEY, code);
  } catch {
    /* ignore persistence failure */
  }
  return true;
};

export const getCustomerTranscriptionLang = () => {
  try {
    const stored = window.localStorage.getItem(CUSTOMER_LANG_KEY);
    return isKnownAgentLang(stored) ? stored : DEFAULT_AGENT_SPEECH_LANGUAGE;
  } catch {
    return DEFAULT_AGENT_SPEECH_LANGUAGE;
  }
};

export const setCustomerTranscriptionLang = (code) => {
  if (!isKnownAgentLang(code)) return false;
  try {
    window.localStorage.setItem(CUSTOMER_LANG_KEY, code);
  } catch {
    /* ignore persistence failure */
  }
  return true;
};
